/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { memo, useMemo, useEffect, useRef, useState } from "react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { convertDate } from "@Services/luxonHelper";
import ComboBoxComponent from "@CubeComponents/form/ComboBoxComponent";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { SaveCurrencyData, DeleteCurrencyData, getSymbolsData } from "@Actions/Currency";
import { showToastNotification } from "@Reducers/toastSlice";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getCurrencyDataUrl } from "@Services/dataManager";
import SlickGrid1, { withDynamicColumns } from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import _ from "lodash";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import { recordStatus } from "@Helpers/constants";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import type { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TranslatedString } from "@i18n";
import type { GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type {
  CurrencyDataItem,
  CurrencySymbolItem,
  DeleteCurrencyRequest,
  GetSymbolsDataRequest,
  SaveCurrencyRequest,
} from "@ServerTypes/currency/currency.schema";

/**
 * One row of the currency-settings grid, derived from the server's row type (`CurrencyDataItem` =
 * `currencyDataItemSchema` in currency.schema).
 *
 * `id` and `createdBy` are re-declared: the schema types both as a bare `z.string()`, but its own
 * descriptions say each is a 24-hex ObjectId, and `createdBy` additionally arrives as the
 * DEFAULT_SYSTEM_USER sentinel `"0"` for seeded currencies — which the `createdBy` cell formatter
 * tests for. Naming those here is what lets the ids flow into getSymbolsData / saveCurrencyData /
 * deleteCurrencyData without a cast at every send site.
 */
type CurrencyRow = GridRowBase &
  Omit<CurrencyDataItem, "id" | "createdBy"> & {
    id?: ObjectIdString;
    createdBy?: ObjectIdString | "0";
  };

/** One `symbols` / `symbolsToAdd` entry of `saveCurrencyRequest` (its `currencySymbolSchema`). */
type SaveCurrencySymbol = NonNullable<SaveCurrencyRequest["symbols"]>[number];

/**
 * One row of the symbol ComboBox's data source. The list is seeded from getSymbolsData
 * (`CurrencySymbolItem` = `{ id, name }`) and appended to locally with freshly typed symbols in the
 * `{ name, isSelectedSymbol }` shape `saveCurrencyRequest.symbols` accepts — so a row carries either
 * set of fields depending on where it came from.
 */
type CurrencySymbolOption = SaveCurrencySymbol & { id?: CurrencySymbolItem["id"] };

/**
 * The symbol currently chosen in the add/edit modal. `{}` before anything is picked; `{ id, name }`
 * once picked from the server list; `{ name, id: undefined }` for a symbol the user just free-typed.
 */
interface CurrencySymbolSelection {
  name?: string;
  id?: ObjectIdString;
}

/** Narrows the chosen symbol to the `Record<string, string>` `saveCurrencyRequest.selectedSymbol` is. */
const toSymbolRecord = (symbol: CurrencySymbolSelection): NonNullable<SaveCurrencyRequest["selectedSymbol"]> =>
  Object.fromEntries(Object.entries(symbol).filter((entry): entry is [string, string] => entry[1] !== undefined));

/**
 * The `query` this grid hands SlickGrid1, which SlickGrid1 spreads into the request `value`.
 *
 * UNDECLARED FIELD, preserved verbatim: `getCurrencyDataRequest.value` (`gridValueSchema`) declares
 * `status` but NOT `params`, so the duplicated `params: { status }` below is stripped by Zod before
 * `getCurrencyData` runs. Nothing is lost — the service reads only `value.status`, which IS declared
 * and IS sent — so the field is kept exactly as it is rather than removed.
 *
 * Written as a type alias, not an interface, so it satisfies the grid's `{ [key: string]: unknown }`
 * `query` prop (only aliases get an implicit index signature).
 */
type CurrencyGridQuery = {
  status: number;
  params: { status: number };
};

/**
 * The `onSelectedRowIdsChanged` payload this grid reads. SlickGrid1 types that callback's argument as
 * an opaque `Record<string, unknown>` (its `SlickEventArgs` alias is not exported), so the nested
 * shape is named here and checked at runtime by `isCurrencySelectionEvent` before it is read.
 * `SlickDataView.items` is `protected` on the real class, but the `dataView` handed to this callback
 * is read as a plain object, so naming `items` here needs no assertion.
 *
 * Written as a type alias, not an interface, so it stays assignable to the prop's
 * `Record<string, unknown>` (only aliases get an implicit index signature) — which is what lets
 * `isCurrencySelectionEvent` be a type predicate over that argument.
 */
type CurrencySelectionEvent = {
  detail: {
    args: {
      rows: number[];
      selectedRowIds: ObjectIdString[];
      dataView: { items: CurrencyRow[] };
    };
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** Runtime check for the selection event above, at the one boundary where SlickGrid1 hands it over. */
const isCurrencySelectionEvent = (event: Record<string, unknown>): event is CurrencySelectionEvent => {
  if (!isRecord(event.detail) || !isRecord(event.detail.args)) return false;
  const { rows, selectedRowIds, dataView } = event.detail.args;
  return Array.isArray(rows) && Array.isArray(selectedRowIds) && isRecord(dataView) && Array.isArray(dataView.items);
};

/**
 * Payload of ComboBoxComponent's `change` callback (see cubeComponent/form/ComboBoxComponent):
 * `value` is the picked option's value field, `itemData` the whole option object — or the raw typed
 * text when the user free-types a brand-new symbol.
 */
interface ComboBoxChangeEvent {
  value: unknown;
  itemData: unknown;
}

/** `itemData` is the picked option object; with `allowCustom` it is the raw typed text instead. */
const isCurrencySymbolOption = (value: unknown): value is CurrencySymbolOption =>
  isRecord(value) && typeof value.name === "string";

/** One entry of the archive / unarchive / delete confirmation-modal table. */
interface ConfirmationAction {
  title: TranslatedString;
  message: TranslatedString;
  handler: () => void;
  confirmText: TranslatedString;
}

const CurrenciesHeader = memo(function CurrenciesHeader({ isActive }: { isActive: boolean }) {
  return (
    <div className="flex flex-col pb-4">
      <GridPageHeaderLeft
        title={isActive ? "Currencies" : "Archived Currencies"}
        description={
          isActive ? "List of all the currencies in this portal" : "List of all the archived currencies in this portal"
        }
      />
    </div>
  );
});

function Currencies() {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<CurrencyRow> | null>(null);
  const dispatch = useDispatch();

  const symbolsRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<number>(recordStatus.ACTIVE);
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [symbols, setSymbols] = useState<CurrencySymbolOption[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<CurrencySymbolSelection>({});
  const [category, setCategory] = useState<string>("Prefix");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [isDefaultChecked, setIsDefaultChecked] = useState<boolean>(false);
  const [symbolsToAdd, setSymbolsToAdd] = useState<SaveCurrencySymbol[]>([]);
  const [query, setQuery] = useState<CurrencyGridQuery>({
    status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
    params: {
      status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
    },
  });
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const [selectedRowIds, setSelectedRowIds] = useState<ObjectIdString[]>([]);
  const [toolBtnState, setToolBtnState] = useState({
    create: true,
    edit: false,
    delete: false,
    archive: false,
    default: false,
    unarchive: false,
  });

  useEffect(() => {
    document.title = t("Portal Settings | Currencies");
  }, []);

  useEffect(() => {
    setQuery({
      status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
      params: {
        status: view === recordStatus.ACTIVE ? recordStatus.ACTIVE : recordStatus.ARCHIVED,
      },
    });
  }, [view]);

  const formatCreatedDate = (data: number, value: unknown, a: unknown, b: unknown, params: CurrencyRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={convertDate(Number(params.createdDate), "dateForTooltip")}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const formatName = (data: number, value: unknown, a: unknown, b: unknown, params: CurrencyRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      params.isDefault ? (
        <div className="flex gap-2 items-center">
          <div>{params.name}</div>
          <div className="rounded-2xl bg-yet_to_start_communication text-white text-xs px-2 py-1">
            <Text>{t("Default")}</Text>
          </div>
        </div>
      ) : (
        <div>{params.name}</div>
      ),
    );
    return container;
  };

  const formatCreatedBy = (data: number, value: unknown, a: unknown, b: unknown, params: CurrencyRow) => {
    const container = document.createElement("div");
    if (params.createdBy && params.createdBy != "0") {
      renderUserTag(container, { id: params.createdBy });
    } else {
      container.textContent = t("System Generated");
    }
    return container;
  };

  // `withDynamicColumns` rather than `typedColumns`: the Symbol column addresses a nested value by
  // dotted path ("selected_currency_symbol.selectedSymbol"), which can never be a `keyof CurrencyRow`.
  const currencyColumnDefinitions = useRef(
    withDynamicColumns<CurrencyRow>([
      {
        id: "name",
        field: "name",
        name: t("Name"),
        filterType: "Text",
        formatter: formatName,
        _valueGetter: (row: CurrencyRow) => (row.name ? (row.isDefault ? `${row.name} (Default)` : row.name) : ""),
      },
      {
        id: "selectedSymbol",
        field: "selected_currency_symbol.selectedSymbol",
        name: t("Symbol"),
        filterType: "Text",
        _valueGetter: (args: CurrencyRow) => _.get(args, "selected_currency_symbol.selectedSymbol"),
      },
      {
        id: "createdDate",
        field: "createdDate",
        name: t("Created On"),
        formatter: formatCreatedDate,
        filterType: "Date",
        _valueGetter: (row: CurrencyRow) =>
          row.createdDate ? convertDate(Number(row.createdDate), "dateForTooltip") : "",
      },
      {
        id: "category",
        field: "category",
        name: t("Prefix/Suffix"),
        filterType: "Text",
      },

      {
        id: "createdByName",
        field: "createdByName",
        name: t("Created by"),
        filterType: "Text",
        formatter: formatCreatedBy,
        _valueGetter: (args: CurrencyRow) => _.get(args, "createdByName"),
      },
    ]),
  );

  const archiveTemplate = () => (
    <SelectDropDown
      buttonType="dropdownBtnCustom"
      select={handleDropDownSelectionHandler}
      items={currencyDropdownButtonsItems}
      allowButtonPadding={false}
      bgColor="bg-surface_white"
      cssClass="e-caret-hide p-0 border-none"
    >
      <IconComponent name="threedots" width="1rem" height="1rem" />
    </SelectDropDown>
  );

  const toolbarOptions = useMemo(() => {
    return view === recordStatus.ACTIVE
      ? [
          {
            name: t("Add"),
            prefixIcon: "add",
            id: "custom_add",
            disabled: !portalSettingInfo.currencies?.addCurrencies?.enable || !toolBtnState.create,
          },
          {
            name: t("Edit"),
            prefixIcon: "editProfile",
            id: "custom_edit",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo.currencies?.editCurrencies?.enable || !toolBtnState.edit,
          },
          {
            name: t("Delete"),
            prefixIcon: "delete",
            id: "custom_delete",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo.currencies?.deleteCurrencies?.enable || !toolBtnState.delete,
          },
          {
            name: t("Archive"),
            prefixIcon: "archive",
            id: "custom_archive",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.archive || !portalSettingInfo.currencies?.editCurrencies?.enable,
          },
          {
            name: t("Set As Default"),
            prefixIcon: "setAsDefault",
            id: "set_default",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.default || !portalSettingInfo.currencies?.editCurrencies?.enable,
          },
          {
            id: "archive_dropdown",
            rightAllign: true,
            template: archiveTemplate,
            disabled: !portalSettingInfo.currencies?.editCurrencies?.enable,
          },
        ]
      : [
          {
            name: t("Unarchive"),
            prefixIcon: "unarchive",
            id: "custom_unarchive",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !toolBtnState.unarchive || !portalSettingInfo.currencies?.editCurrencies?.enable,
          },
          {
            name: t("Delete"),
            prefixIcon: "delete",
            id: "custom_delete",
            buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
            disabled: !portalSettingInfo.currencies?.deleteCurrencies?.enable || !toolBtnState.delete,
          },
          {
            id: "archive_dropdown",
            rightAllign: true,
            template: archiveTemplate,
            disabled: !portalSettingInfo.currencies?.editCurrencies?.enable,
          },
        ];
  }, [view, toolBtnState, portalSettingInfo.currencies]);

  const toolbarClickHandler = (args: string) => {
    if (args === "custom_add") {
      resetFormData();
      setOpenAddEditModal(true);
    } else if (args === "custom_edit") {
      // The Edit toolbar button is only enabled with exactly one row selected, and every grid row
      // carries id/name/category — the `!`s stand for that and keep today's crash if it ever doesn't.
      const data = dataGrid.current!.dataView.getItem(dataGrid.current!.slickGrid.getSelectedRows()[0]!);
      setName(data.name!);
      getSymbolsDataForEditComboBox({ id: data.id! });
      setCategory(data.category!);
      setEditMode(true);
      setOpenAddEditModal(true);
    } else if (args === "custom_delete") {
      setConfirmAction("delete");
    } else if (args === "set_default") {
      const currentSelectedId = dataGrid.current!.slickGrid.getSelectedRows()[0]!;
      const currentSelectedItem = dataGrid.current!.dataView.getItem(currentSelectedId);
      const data: SaveCurrencyRequest = {
        isDefault: true,
        _id: { $oid: currentSelectedItem.id! },
      };
      handleEditData(data);
    } else if (args === "custom_archive") {
      setConfirmAction("archive");
    } else if (args === "custom_unarchive") {
      setConfirmAction("unarchive");
    }
  };

  const handleDropDownSelectionHandler = async (args: { item: { id?: string } }) => {
    if (args.item.id === "archive_currency") {
      setView(recordStatus.ARCHIVED);
    } else if (args.item.id === "active_currency") {
      setView(recordStatus.ACTIVE);
    }
  };

  const currencyDropdownButtonsItems = useMemo(
    () => [
      {
        text: view === recordStatus.ACTIVE ? t("Show Archived Currencies") : t("Show Active Currencies"),
        id: view === recordStatus.ACTIVE ? "archive_currency" : "active_currency",
      },
    ],
    [view],
  );

  const selectingEvents = (event: Record<string, unknown>) => {
    if (!dataGrid.current || !isCurrencySelectionEvent(event)) return;

    const { rows, selectedRowIds, dataView } = event.detail.args;
    const totalRowsSelected = rows.length;
    setSelectedRowIds(selectedRowIds);
    const isDefaultSelected = dataView.items.some(
      (item) => selectedRowIds.includes(item.id!) && item.isDefault === true,
    );
    const isSystemGeneratedSelected = dataView.items.some(
      (item) => selectedRowIds.includes(item.id!) && (!item.createdBy || item.createdBy === "0"),
    );

    if (totalRowsSelected == 0) {
      setToolBtnState({
        create: true,
        delete: false,
        edit: false,
        archive: false,
        default: false,
        unarchive: false,
      });
    } else if (totalRowsSelected == 1) {
      setToolBtnState({
        create: true,
        delete: !isDefaultSelected && !isSystemGeneratedSelected,
        edit: true,
        archive: !isDefaultSelected,
        default: !isDefaultSelected,
        unarchive: true,
      });
    } else if (totalRowsSelected > 1) {
      setToolBtnState({
        create: true,
        delete: !isDefaultSelected && !isSystemGeneratedSelected,
        edit: false,
        archive: !isDefaultSelected,
        default: false,
        unarchive: true,
      });
    }
  };

  const getSymbolsDataForEditComboBox = async (data: GetSymbolsDataRequest) => {
    const response = await getSymbolsData(data);
    if (response.data.status) {
      setSymbols(response.data.data ?? []);
      // KNOWN BUG, preserved verbatim: `selectedSymbolData` is optional in `getSymbolsDataResponse`
      // and is stored unguarded, so a currency whose selectedSymbol pointer does not resolve leaves
      // this state `undefined` — and the ComboBox below then reads `selectedSymbol.name` and throws.
      // The `!` keeps that. Fix: `response.data.selectedSymbolData ?? {}`.
      setSelectedSymbol(response.data.selectedSymbolData!);
    }
  };

  const handleSaveData = async () => {
    if (!editMode && symbols.length && name !== "" && category !== "") {
      const symbolsData: SaveCurrencySymbol[] = [];
      symbols.map((symbol) => {
        symbolsData.push(
          symbol.name === selectedSymbol.name
            ? { name: symbol.name, isSelectedSymbol: true }
            : { name: symbol.name, isSelectedSymbol: false },
        );
      });
      const data: SaveCurrencyRequest = {
        name,
        symbols: symbolsData,
        category,
        isDefault: !!isDefaultChecked,
      };
      const response = await SaveCurrencyData(data);
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("add_currency", { currency_name: data.name }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current!.refresh!();
        resetFormData();
        setOpenAddEditModal(false);
      }
    }
    if (editMode && symbols.length && name !== "" && category !== "") {
      const currentSelectedItem = dataGrid.current!.dataView.getItem(dataGrid.current!.slickGrid.getSelectedRows()[0]!);
      const data: SaveCurrencyRequest = {
        name,
        category,
        // KNOWN BUG, preserved verbatim: `saveCurrencyRequest.selectedSymbol` is the extended-JSON
        // wrapper `{ $oid: <symbol _id> }`, and `handleEditData` below only rewrites this value into
        // that wrapper when the chosen symbol HAS an `id`. A symbol the user just free-typed has
        // `id: undefined` (see `addNewSymbol`), so the raw `{ name }` object is sent instead and the
        // server stores a glyph where a symbol-ObjectId reference belongs. Fix: send `symbolsToAdd`
        // alone in that case and let the server-generated _id become the selected symbol.
        //
        // The schema types this field as an open string->string record, so the unset keys are
        // dropped here rather than sent as `undefined` (which JSON serialisation dropped anyway).
        selectedSymbol: toSymbolRecord(selectedSymbol),
        _id: { $oid: currentSelectedItem.id! },
      };
      if (symbolsToAdd.length) {
        const additionalSymbol: SaveCurrencySymbol[] = [];
        symbolsToAdd.map((symbol) => {
          additionalSymbol.push(
            symbol.name === selectedSymbol.name
              ? { name: symbol.name, isSelectedSymbol: true }
              : { name: symbol.name, isSelectedSymbol: false },
          );
        });
        data.symbolsToAdd = additionalSymbol;
      }
      handleEditData(data);
    }
  };

  const handleDelete = async () => {
    const dataObj: DeleteCurrencyRequest = { id: selectedRowIds };
    const response = await DeleteCurrencyData(dataObj);
    if (response.data.status) {
      if (!response.data.CannotBeDeleted && response.data.currencyName) {
        if (response.data.currencyName.length == 1) {
          dispatch(
            showToastNotification({
              content: t("delete_currency", { currency_name: response.data.currencyName[0]?.currencyName }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t("delete_currencies", {
                currency_names: response.data.currencyName.map((curr) => curr.currencyName).join(", "),
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }
        dataGrid.current!.refresh!();
        setView(recordStatus.ACTIVE);
      } else if (response.data.CannotBeDeleted) {
        dispatch(
          showToastNotification({
            content: t("One of the selected currency is already in use and hence it cannot be deleted"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const handleArchiveAction = async (action: string) => {
    const selectedIds = selectedRowIds;
    const isArchiving = action === "archive";

    const dataObj: SaveCurrencyRequest = {
      _id: selectedIds.map((id) => ({ $oid: id })),
      recordStatus: isArchiving ? recordStatus.ARCHIVED : recordStatus.ACTIVE,
    };

    const response = await SaveCurrencyData(dataObj);

    if (response.data.status) {
      const cannotProcess = isArchiving ? response.data.CannotBeArchived : false;

      if (!cannotProcess && response.data.currencyData) {
        const actionKey = isArchiving ? "archive" : "unarchive";
        const actionKeyPlural = isArchiving ? "archive" : "unarchive";

        if (response.data.currencyData.length === 1) {
          dispatch(
            showToastNotification({
              content: t(`${actionKey}_currency`, { currency_name: response.data.currencyData[0] }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t(`${actionKeyPlural}_currencies`, {
                currency_names: response.data.currencyData.join(", "),
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }

        if (!isArchiving) {
          setView(recordStatus.ACTIVE);
        }
      } else if (cannotProcess) {
        if (response.data.isDefault) {
          dispatch(
            showToastNotification({
              content: t("default_archive_currency", { currency_name: response.data.currencyData?.[0] ?? "" }),
              type: TOAST_TYPE_ERROR,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: t("The Currency(s) cannot be archived as they are already in use."),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      }

      dataGrid.current!.refresh!();
    }
  };

  const handleEditData = async (data: SaveCurrencyRequest) => {
    if (data.selectedSymbol?.id) {
      data.selectedSymbol = { $oid: data.selectedSymbol.id! };
    }
    const response = await SaveCurrencyData(data);
    if (response.data.status && editMode) {
      dispatch(
        showToastNotification({
          content: t("edit_currency", { currency_name: data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    if (response.data.status && response.data.isDefault && !editMode) {
      dispatch(
        showToastNotification({
          content: t("default_currency", { currency_name: response.data.currencyData?.[0] ?? "" }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    resetFormData();
    setOpenAddEditModal(false);
    dataGrid.current!.refresh!();
  };

  const addNewSymbol = (e: ComboBoxChangeEvent) => {
    if (e.value) {
      const { itemData } = e;
      const pickedOption = isCurrencySymbolOption(itemData) ? itemData : undefined;
      const text = typeof itemData === "string" ? itemData : pickedOption?.name;
      const textId = typeof itemData === "string" ? undefined : pickedOption?.id;
      if (text === "" || !text) return;
      const trimmedText = text.trim();
      const symbolFound = symbols.length > 0 ? symbols.find((record) => record.name === trimmedText) : false;
      if (symbolFound) {
        setSelectedSymbol({ name: trimmedText, id: textId });
      } else {
        setSelectedSymbol({ name: trimmedText, id: textId });
        setSymbols([...symbols, { name: trimmedText, isSelectedSymbol: false }]);

        if (editMode) {
          setSymbolsToAdd([...symbolsToAdd, { name: trimmedText, isSelectedSymbol: false }]);
        }
      }
    } else {
      setSelectedSymbol({});
    }
  };

  const getSymbolsCombobox = () => (
    <ComboBoxComponent
      ref={symbolsRef}
      allowCustom
      autoSelect
      placeholder={t("Select Symbol")}
      change={addNewSymbol}
      dataSource={symbols}
      value={selectedSymbol.name}
      fields={{ value: "name", text: "name" }}
    />
  );

  const resetFormData = () => {
    setName("");
    setSymbols([]);
    setSelectedSymbol({});
    setCategory("Prefix");
    setIsDefaultChecked(false);
    setEditMode(false);
    setSymbolsToAdd([]);
  };

  const addEditPopup = () => {
    const modalContent = (
      <div className="flex flex-col gap-5" style={{ width: "18.75rem" }}>
        <div>
          <Text className="pb-2 text-sm font-medium">{t("Currency Name")}</Text>
          <TextFieldComponent onChange={(e) => setName(e.target.value)} value={name} placeholder={t("Add Name")} />
        </div>

        <div>
          <Text className="pb-2 text-sm font-medium">{t("Symbols")}</Text>
          {getSymbolsCombobox()}
        </div>

        <div>
          <Text className="pb-4 text-sm font-medium">{t("Category")}</Text>
          <div className="flex gap-6">
            <RadioButton
              checked={category === "Prefix"}
              onClick={() => setCategory("Prefix")}
              label={t("Prefix")}
              name="category"
            />
            <RadioButton
              checked={category === "Suffix"}
              onClick={() => setCategory("Suffix")}
              label={t("Suffix")}
              name="category"
            />
          </div>
        </div>

        {!editMode && (
          <div>
            <CheckBoxComponent
              label={t("Set As Default Currency")}
              checked={isDefaultChecked}
              onChange={() => setIsDefaultChecked(!isDefaultChecked)}
            />
          </div>
        )}
      </div>
    );
    const handleCancel = () => {
      resetFormData();
      setOpenAddEditModal(false);
    };

    const footerContent = (
      <div className="flex gap-4">
        <ButtonComponent
          disabled={!(name.trim() !== "" && symbols.length > 0 && category !== "")}
          buttonType="gradientButton"
          onClick={handleSaveData}
        >
          {editMode ? t("Save") : t("Create")}
        </ButtonComponent>
        <ButtonComponent onClick={handleCancel} buttonType="borderButton">
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    return (
      <ModalStateDialogComponent
        headerTitle={editMode ? t("Edit Currency") : t("Add New Currency")}
        modelContent={modalContent}
        footerContent={footerContent}
        onCancel={handleCancel}
        isVisible={openAddEditModal}
        width="400"
      />
    );
  };

  const confirmationActions: Record<string, ConfirmationAction> = {
    archive: {
      title: t("Archive Currency"),
      message: t("Are you sure you want to archive the selected currency(s)?"),
      handler: () => handleArchiveAction("archive"),
      confirmText: t("Confirm"),
    },
    unarchive: {
      title: t("Unarchive Currency"),
      message: t("Are you sure you want to unarchive the selected currency(s)?"),
      handler: () => handleArchiveAction("unarchive"),
      confirmText: t("Confirm"),
    },
    delete: {
      title: t("Delete Currency"),
      message: t("Are you sure you want to delete the selected currency(s)?"),
      handler: handleDelete,
      confirmText: t("Delete"),
    },
  };

  const confirmationPopup = () => {
    if (!confirmAction) return null;

    // `confirmAction` is only ever set to one of the three keys above, so the lookup always hits.
    const action = confirmationActions[confirmAction]!;

    return (
      <ModalStateDialogComponent
        headerTitle={action.title}
        modelContent={action.message}
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={() => {
                action.handler();
                setConfirmAction(null);
              }}
              buttonType="gradientButton"
            >
              <Text>{action.confirmText}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={() => setConfirmAction(null)} buttonType="borderButton">
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={() => setConfirmAction(null)}
        isVisible={!!confirmAction}
        width={confirmAction === "delete" ? undefined : "440"}
      />
    );
  };

  const getDataGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      columnDefinitions={currencyColumnDefinitions.current}
      remoteDataSource={getCurrencyDataUrl}
      query={query}
      infiniteScroll
      enableRowResize
      enableFilteringOption
      enableColumnChooser
      enableGroupingOption
      toolbarClickHandler={toolbarClickHandler}
      toolbar={toolbarOptions}
      onSelectedRowIdsChanged={selectingEvents}
      enableSortingOption
      excelExport
    />
  );

  return (
    <>
      <GridPageLayout customHeader={<CurrenciesHeader isActive={view === recordStatus.ACTIVE} />}>
        {getDataGrid()}
      </GridPageLayout>
      {openAddEditModal && view === recordStatus.ACTIVE ? addEditPopup() : null}
      {confirmationPopup()}
    </>
  );
}

export default Currencies;
